import { toHTML } from "slack-markdown";
/**
 * slack-markdown only recognizes block quotes as `> ` (space after `>`).
 * Slack payloads often use `>text` or `>• text`; normalize without touching `>>` nesting.
 */
export function normalizeSlackMrkdwnBlockQuotes(text: string): string {
    return text.replace(/^(\s*)>(?=[^\s>\n])/gm, "$1> ");
}

/** Normalize line endings so paragraph-break rules (e.g. slack-markdown blockquotes) match `\n`. */
export function normalizeSlackPreviewNewlines(text: string): string {
    return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

/**
 * slack-markdown ends a blockquote at a paragraph break (`\n\n`). Collapse extra blank lines
 * before a new `>…` line so consecutive quoted bullets stay one block (continuous left rule).
 * Expect LF-only input (run {@link normalizeSlackPreviewNewlines} first): `\r\n\r\n` does not match `\n\n+`.
 */
export function collapseBlankLinesBeforeBlockquoteLines(text: string): string {
    return text.replace(/\n\n+(?=>)/g, "\n");
}

/** Private-use chars unlikely in stored messages; used as ATX heading placeholders for slack-markdown. */
const ATX_HDR_PREFIX = "\uE000";
const ATX_HDR_SUFFIX = "\uE001";
/** Placeholders so slack-markdown does not truncate `$` in Workday-style URLs. */
const LINK_PREFIX = "\uE002";
const LINK_SUFFIX = "\uE003";
/** Whole-item placeholders for nested `•` lists so wraps stay hanging-indented. */
const LIST_ITEM_PREFIX = "\uE007";
const LIST_ITEM_SUFFIX = "\uE008";

export type SlackMrkdwnAtxHeader = { level: number; title: string };
export type SlackMrkdwnLink = { url: string; label: string };
export type SlackMrkdwnListItem = { indent: number; content: string };

const slackMrkdwnLinkPattern = /<(https?:\/\/[^>|]+)(?:\|([^>]+))?>|(https?:\/\/[^\s<>]+)/g;

export function maskSlackMrkdwnLinks(text: string): { text: string; links: SlackMrkdwnLink[] } {
    const links: SlackMrkdwnLink[] = [];
    const lines = text.split(/\r?\n/);
    let inFence = false;
    const out: string[] = [];

    for (const line of lines) {
        if (/^\s*```/.test(line)) {
            out.push(line);
            inFence = !inFence;
            continue;
        }
        if (inFence) {
            out.push(line);
            continue;
        }

        out.push(
            line.replace(slackMrkdwnLinkPattern, (match, labeledUrl?: string, label?: string, bareUrl?: string) => {
                let url = labeledUrl || bareUrl || "";
                let trailing = "";
                if (!labeledUrl && bareUrl) {
                    url = bareUrl.replace(/[.,;:)\]]+$/, "");
                    trailing = bareUrl.slice(url.length);
                }

                try {
                    const parsed = new URL(url);
                    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
                        return match;
                    }
                } catch {
                    return match;
                }

                const idx = links.length;
                const display = label?.trim() ? label : url;
                links.push({ url, label: display });
                return `${LINK_PREFIX}${idx}${LINK_SUFFIX}${trailing}`;
            })
        );
    }

    return { text: out.join("\n"), links };
}

export function unmaskSlackMrkdwnLinksInHtml(html: string, links: SlackMrkdwnLink[]): string {
    let result = html;
    for (let i = 0; i < links.length; i++) {
        const marker = `${LINK_PREFIX}${i}${LINK_SUFFIX}`;
        const href = escapeHtmlAttribute(links[i].url);
        const label = escapeHtmlText(links[i].label);
        const anchor = `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`;
        result = result.split(marker).join(anchor);
    }
    return result;
}

/**
 * slack-markdown does not implement ATX headings (# … ###### …).
 * Mask whole-line headings (1–6 hashes, space, title) so the preview can emit real h1–h6.
 * Lines inside ``` fenced blocks are left unchanged.
 */
export function maskSlackMrkdwnAtxHeaders(text: string): { text: string; atxHeaders: SlackMrkdwnAtxHeader[] } {
    const atxHeaders: SlackMrkdwnAtxHeader[] = [];
    const lines = text.split(/\r?\n/);
    let inFence = false;
    const out: string[] = [];

    for (const line of lines) {
        if (/^\s*```/.test(line)) {
            out.push(line);
            inFence = !inFence;
            continue;
        }
        if (inFence) {
            out.push(line);
            continue;
        }
        const m = /^(\s*)(#{1,6})(?!#)\s+(.+)$/.exec(line);
        if (!m) {
            out.push(line);
            continue;
        }
        const level = m[2].length;
        const title = m[3].trimEnd().replace(/\s+#+\s*$/, "");
        const idx = atxHeaders.length;
        atxHeaders.push({ level, title });
        out.push(`${m[1]}${ATX_HDR_PREFIX}${idx}${ATX_HDR_SUFFIX}`);
    }

    return { text: out.join("\n"), atxHeaders };
}

export function unmaskSlackMrkdwnAtxHeadersInHtml(
    html: string,
    atxHeaders: SlackMrkdwnAtxHeader[],
    toHtmlOpts: { hrefTarget?: string; escapeHTML?: boolean }
): string {
    let result = html;
    for (let i = 0; i < atxHeaders.length; i++) {
        const marker = `${ATX_HDR_PREFIX}${i}${ATX_HDR_SUFFIX}`;
        const { level, title } = atxHeaders[i];
        const inner = toHTML(title, {
            hrefTarget: toHtmlOpts.hrefTarget ?? "_blank",
            escapeHTML: toHtmlOpts.escapeHTML !== false
        });
        const tag = `h${level}`;
        const block = `<${tag} class="ma-ae-slack-atx-h ma-ae-slack-atx-h--${level}">${inner}</${tag}>`;
        result = result.split(marker).join(block);
    }
    return result;
}

function leadingWsWidth(line: string): number {
    const m = /^[\t \u00a0]*/.exec(line);
    return m ? m[0].length : 0;
}

function matchBulletListLine(line: string): { pad: number; content: string } | null {
    const m = /^([\t \u00a0]*)•[ \u00a0](.*)$/.exec(line);
    if (!m) {
        return null;
    }
    return { pad: m[1].length, content: m[2] };
}

function lineIsListContinuation(line: string, bulletPad: number): boolean {
    if (bulletPad <= 0 || matchBulletListLine(line) || /^\s*```/.test(line) || /^[\t \u00a0]*>/.test(line)) {
        return false;
    }
    return leadingWsWidth(line) >= bulletPad;
}

/**
 * Pull nested `•` items (and indented follow-on lines in the same item) out of mrkdwn
 * so the preview can emit hanging-indent HTML. Indent-0 bullets stay one item and do
 * not swallow following flush-left lines (footer, Workday links).
 */
export function maskSlackMrkdwnBulletListItems(text: string): { text: string; listItems: SlackMrkdwnListItem[] } {
    const listItems: SlackMrkdwnListItem[] = [];
    const lines = text.split("\n");
    const out: string[] = [];
    let inFence = false;

    for (let i = 0; i < lines.length; ) {
        const line = lines[i];
        if (/^\s*```/.test(line)) {
            inFence = !inFence;
            out.push(line);
            i++;
            continue;
        }
        if (inFence) {
            out.push(line);
            i++;
            continue;
        }

        const bullet = matchBulletListLine(line);
        if (!bullet) {
            out.push(line);
            i++;
            continue;
        }

        const contentLines = [bullet.content];
        i++;
        while (i < lines.length) {
            const next = lines[i];
            if (/^\s*```/.test(next) || matchBulletListLine(next)) {
                break;
            }
            if (!next.trim()) {
                let j = i + 1;
                while (j < lines.length && !lines[j].trim()) {
                    j++;
                }
                if (j < lines.length && lineIsListContinuation(lines[j], bullet.pad)) {
                    contentLines.push("");
                    i++;
                    continue;
                }
                break;
            }
            if (!lineIsListContinuation(next, bullet.pad)) {
                break;
            }
            contentLines.push(next.trim());
            i++;
        }

        const idx = listItems.length;
        listItems.push({
            indent: Math.floor(bullet.pad / 4),
            content: contentLines.join("\n")
        });
        out.push(`${LIST_ITEM_PREFIX}${idx}${LIST_ITEM_SUFFIX}`);
    }

    return { text: out.join("\n"), listItems };
}

function listBulletGlyph(indent: number): string {
    if (indent <= 0) {
        return "•";
    }
    if (indent === 1) {
        return "◦";
    }
    return "▪";
}

export function unmaskSlackMrkdwnBulletListItemsInHtml(
    html: string,
    listItems: SlackMrkdwnListItem[],
    links: SlackMrkdwnLink[],
    atxHeaders: SlackMrkdwnAtxHeader[],
    toHtmlOpts: { hrefTarget?: string; escapeHTML?: boolean }
): string {
    let result = html;
    for (let i = 0; i < listItems.length; i++) {
        const marker = `${LIST_ITEM_PREFIX}${i}${LIST_ITEM_SUFFIX}`;
        const { indent, content } = listItems[i];
        let inner = toHTML(content, {
            hrefTarget: toHtmlOpts.hrefTarget ?? "_blank",
            escapeHTML: toHtmlOpts.escapeHTML !== false
        });
        inner = unmaskSlackMrkdwnAtxHeadersInHtml(inner, atxHeaders, toHtmlOpts);
        inner = unmaskSlackMrkdwnLinksInHtml(inner, links);
        const safeIndent = Math.max(0, Math.min(8, indent));
        const glyph = listBulletGlyph(safeIndent);
        const block =
            `<div class="ma-ae-slack-rt-li ma-ae-slack-rt-li--indent-${safeIndent}" style="--ma-ae-rt-indent:${safeIndent}">` +
            `<span class="ma-ae-slack-rt-li__bullet" aria-hidden="true">${glyph}</span>` +
            `<span class="ma-ae-slack-rt-li__text">${inner}</span></div>`;
        result = result.split(marker).join(block);
    }
    result = result.replace(/<br>\s*(?=<div class="ma-ae-slack-rt-li\b)/gi, "");
    result = result.replace(/(<\/div>)\s*<br>(?=<div class="ma-ae-slack-rt-li\b)/gi, "$1");
    return result;
}

/**
 * slack-markdown keeps `state.inQuote` true after the first blockquote parse, so later `> …`
 * lines are emitted as escaped `&gt;…` text. Re-wrap those runs as real blockquotes.
 */
export function repairEscapedMrkdwnBlockquotesInHtml(html: string): string {
    const pieces = html.split("<br>");
    const rebuilt: string[] = [];
    for (let i = 0; i < pieces.length; ) {
        const seg = pieces[i];
        if (/^&gt;\s+/.test(seg)) {
            const quoteLines: string[] = [];
            while (i < pieces.length && /^&gt;\s+/.test(pieces[i])) {
                quoteLines.push(pieces[i].replace(/^&gt;\s+/, ""));
                i++;
            }
            rebuilt.push(`<blockquote>${quoteLines.join("<br>")}</blockquote>`);
        } else {
            rebuilt.push(seg);
            i++;
        }
    }
    return rebuilt.join("<br>");
}

/**
 * slack-markdown emits a flat `<br>` stream; repaired blockquotes and blank mrkdwn lines
 * can produce 3+ consecutive breaks that read as oversized gaps. Trims trailing breaks
 * inside blockquotes, then collapses any run of 3+ breaks to a single line break
 * (double newlines in source still map to `<br><br>` and are preserved).
 */
export function compactSlackDeliveryPreviewHtmlBreaks(html: string): string {
    let s = html.replace(/(?:<br>\s*)+(?=<\/blockquote>)/gi, "");
    s = s.replace(/(?:<br>\s*){3,}/gi, "<br>");
    return s;
}

const BoxDrawingLightHorizontal = "\u2500";

const SLACK_SUMMARY_CARD_ICON_URL =
    "https://avatars.slack-edge.com/2026-03-04/10630077369814_798fb5e4e42b0b90f4db_32.png";
const SLACK_SUMMARY_CARD_TITLE = "AskGM Notifications";
const SLACK_SUMMARY_CARD_TITLE_DIGEST = "AskGM Notifications - Summary";
const SUMMARY_COUNT_SUBTITLE_PATTERN =
    /^You have \*\d+ pending item(?:s)?\*\s+across \*\d+ system(?:s)?\*\./;
export const SLACK_IMMEDIATE_SUBJECT_SUBTITLE_MAX_LENGTH = 40;

export type ParsedActivityMessageCardHeader = {
    title: string;
    subtitle?: string;
    body: string;
    isImmediateSubject?: boolean;
};

function stripLeadingLineBreaks(text: string, maxBreaks = 2): string {
    let s = text;
    for (let i = 0; i < maxBreaks && s.length > 0; i++) {
        if (s.startsWith("\r\n")) {
            s = s.slice(2);
        } else if (s[0] === "\n" || s[0] === "\r") {
            s = s.slice(1);
        } else {
            break;
        }
    }
    return s;
}

function stripLegacyBacktickHeader(text: string): { stripped: string; title?: string } {
    const s = text.trimStart();
    if (s.length < 2 || s[0] !== "`") {
        return { stripped: text };
    }

    const close = s.indexOf("`", 1);
    if (close <= 1) {
        return { stripped: text };
    }

    return {
        title: s.slice(1, close),
        stripped: stripLeadingLineBreaks(s.slice(close + 1))
    };
}

function stripBoldCardTitle(text: string): { stripped: string; title?: string } {
    for (const title of [SLACK_SUMMARY_CARD_TITLE_DIGEST, SLACK_SUMMARY_CARD_TITLE]) {
        const wrapped = `*${title}*`;
        if (!text.startsWith(wrapped)) {
            continue;
        }

        return {
            title,
            stripped: stripLeadingLineBreaks(text.slice(wrapped.length))
        };
    }

    return { stripped: text };
}

function stripSummaryCountSubtitle(text: string): { stripped: string; subtitle?: string } {
    const match = SUMMARY_COUNT_SUBTITLE_PATTERN.exec(text);
    if (!match) {
        return { stripped: text };
    }

    return {
        subtitle: match[0].trim(),
        stripped: stripLeadingLineBreaks(text.slice(match[0].length))
    };
}

/** First line before a blank line, when not a batch summary count subtitle. */
function stripImmediateCardSubject(text: string): { stripped: string; subtitle?: string } {
    const trimmed = text.trimStart();
    const blankLine = trimmed.indexOf("\n\n");
    if (blankLine <= 0) {
        return { stripped: text };
    }

    const candidate = trimmed.slice(0, blankLine).trim();
    if (!candidate || candidate.includes("\n") || SUMMARY_COUNT_SUBTITLE_PATTERN.test(candidate)) {
        return { stripped: text };
    }

    return {
        subtitle: candidate,
        stripped: stripLeadingLineBreaks(trimmed.slice(blankLine))
    };
}

/** Separates stored activity text into card title/subtitle (if present) and mrkdwn body for preview. */
export function parseActivityMessageCardHeader(raw: string): ParsedActivityMessageCardHeader {
    const lf = normalizeSlackPreviewNewlines(raw).trimStart();
    const legacy = stripLegacyBacktickHeader(lf);
    const bold = stripBoldCardTitle(legacy.stripped);
    const title = bold.title ?? legacy.title ?? SLACK_SUMMARY_CARD_TITLE;
    const summarySubtitle = stripSummaryCountSubtitle(bold.stripped);
    const immediateSubject = summarySubtitle.subtitle
        ? { stripped: summarySubtitle.stripped }
        : stripImmediateCardSubject(summarySubtitle.stripped);

    return {
        title,
        subtitle: summarySubtitle.subtitle ?? immediateSubject.subtitle,
        body: immediateSubject.stripped.trimStart(),
        isImmediateSubject: !summarySubtitle.subtitle && !!immediateSubject.subtitle
    };
}

export function buildSlackCardHeaderPreviewHtml(
    title: string,
    subtitle?: string,
    emojiMap?: Readonly<Record<string, string>>,
    isImmediateSubject = false
): string {
    const useFullWidthSubtext =
        !!subtitle &&
        isImmediateSubject &&
        subtitle.length > SLACK_IMMEDIATE_SUBJECT_SUBTITLE_MAX_LENGTH;

    let textContent: string;
    let fullWidthSecondary = "";
    if (subtitle && useFullWidthSubtext) {
        const subtextHtml = slackMrkdwnFragmentToPreviewHtml(subtitle, emojiMap);
        textContent = `<div class="ma-ae-slack-preview-card__title">${escapeHtmlAttribute(title)}</div>`;
        fullWidthSecondary = `<div class="ma-ae-slack-preview-card__subtext">${subtextHtml}</div>`;
    } else if (subtitle) {
        const subtitleHtml = slackMrkdwnFragmentToPreviewHtml(subtitle, emojiMap);
        textContent = `<div class="ma-ae-slack-preview-card__title">${escapeHtmlAttribute(title)}</div><div class="ma-ae-slack-preview-card__subtitle">${subtitleHtml}</div>`;
    } else {
        textContent = `<div class="ma-ae-slack-preview-card__title">${escapeHtmlAttribute(title)}</div>`;
    }

    const iconUrl = escapeHtmlAttribute(SLACK_SUMMARY_CARD_ICON_URL);

    return `<div class="ma-ae-slack-preview-card" role="presentation">
<div class="ma-ae-slack-preview-card__icon" aria-hidden="true">
<img class="ma-ae-slack-preview-card__icon-img" src="${iconUrl}" alt="" width="36" height="36" loading="lazy" decoding="async" onerror="this.classList.add('ma-ae-slack-preview-card__icon-img--hidden')" />
<span class="ma-ae-slack-preview-card__icon-mark"><span class="ma-ae-slack-preview-card__icon-mark-a">A</span>sk</span>
</div>
<div class="ma-ae-slack-preview-card__text">
${textContent}
</div>${fullWidthSecondary}
</div>`;
}

export function slackMrkdwnFragmentToPreviewHtml(
    fragment: string,
    emojiMap?: Readonly<Record<string, string>>
): string {
    return convertSlackMrkdwnToPreviewHtml(fragment, emojiMap);
}

/**
 * Activity log digest text inserts {@link https://unicode-table.com/en/2500/ U+2500} runs between app groups
 * (see SlackConstants.SlackActivityLogAppGroupDivider). slack-markdown wraps that as {@code <br>────…<br>}; swap for a semantic rule in the preview only.
 */
export function replaceActivityLogAppDividerLineWithHr(html: string): string {
    const barRun = `${BoxDrawingLightHorizontal}{8,}`;
    return html.replace(
        new RegExp(`<br>\\s*(${barRun})\\s*<br>`, "gi"),
        '<hr class="ma-ae-slack-preview-hr" />'
    );
}

const slackEmojiNamePattern = /:([a-zA-Z0-9_\-+]+):/g;
const slackCodeOrPreBlockPattern = /(<(?:pre|code)\b[^>]*>[\s\S]*?<\/(?:pre|code)>)/gi;

let slackEmojiMapPromise: Promise<Readonly<Record<string, string>>> | null = null;

function escapeHtmlAttribute(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

function escapeHtmlText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** Loads workspace emoji short name → image URL (cached for the session). */
export function ensureSlackEmojiMapLoaded(baseUrl: string): Promise<Readonly<Record<string, string>>> {
    if (typeof fetch !== "function") {
        return Promise.resolve({});
    }
    if (!slackEmojiMapPromise) {
        const url = `${baseUrl}/ActivityEvents/GetSlackEmojis`;
        slackEmojiMapPromise = fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    return {};
                }
                const data = (await response.json()) as Record<string, string>;
                return data ?? {};
            })
            .catch(() => ({}));
    }
    return slackEmojiMapPromise;
}

/** Replaces unresolved :emoji: tokens with workspace custom emoji images. */
export function applySlackWorkspaceEmojisToHtml(
    html: string,
    emojiMap: Readonly<Record<string, string>> | undefined
): string {
    if (!emojiMap || Object.keys(emojiMap).length === 0) {
        return html;
    }

    const segments = html.split(slackCodeOrPreBlockPattern);
    return segments
        .map((segment, index) => {
            if (index % 2 === 1) {
                return segment;
            }
            return segment.replace(slackEmojiNamePattern, (match, name: string) => {
                const imageUrl = emojiMap[name];
                if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
                    return match;
                }
                const src = escapeHtmlAttribute(imageUrl);
                const alt = escapeHtmlAttribute(`:${name}:`);
                return `<img class="ma-ae-slack-emoji" src="${src}" alt="${alt}" title="${alt}" loading="lazy" decoding="async" />`;
            });
        })
        .join("");
}

export type ImmediatePreviewParts = { subject?: string; body: string };

function unwrapMrkdwnBold(text: string): string {
    const trimmed = text.trim();
    const wrapped = /^\*(.+)\*$/.exec(trimmed);
    return wrapped ? wrapped[1] : trimmed;
}

function isAskGmCardTitle(title: string): boolean {
    return title === SLACK_SUMMARY_CARD_TITLE || title === SLACK_SUMMARY_CARD_TITLE_DIGEST;
}

function stripLeadingSubjectFromBody(body: string, subject: string): string {
    const trimmed = body.trimStart();
    const candidates = [subject, `*${subject}*`, `*${unwrapMrkdwnBold(subject)}*`];
    for (const candidate of candidates) {
        if (trimmed === candidate) {
            return "";
        }
        const prefix = `${candidate}\n\n`;
        if (trimmed.startsWith(prefix)) {
            return trimmed.slice(prefix.length).trimStart();
        }
    }
    return trimmed;
}

/** Immediate preview: subject as container title when present; otherwise body only (no AskGM card). */
export function resolveImmediatePreviewParts(raw: string, explicitSubject?: string): ImmediatePreviewParts {
    const lf = normalizeSlackPreviewNewlines(raw).trimStart();
    const legacy = stripLegacyBacktickHeader(lf);
    const bold = stripBoldCardTitle(legacy.stripped);
    const afterTitle = stripLeadingLineBreaks(bold.stripped);
    const afterCount = stripSummaryCountSubtitle(afterTitle);
    if (afterCount.subtitle) {
        return { body: afterCount.stripped.trimStart() };
    }

    const newStyle = /^\*([^*\n]+)\*\n\n([\s\S]*)$/.exec(afterTitle);
    if (newStyle && !isAskGmCardTitle(newStyle[1].trim())) {
        return { subject: newStyle[1].trim(), body: newStyle[2].trimStart() };
    }

    const explicit = explicitSubject?.trim();
    if (explicit) {
        return { subject: unwrapMrkdwnBold(explicit), body: stripLeadingSubjectFromBody(afterTitle, explicit) };
    }

    return { body: afterTitle.trimStart() };
}

function renderSlackBodyHtml(body: string, emojiMap?: Readonly<Record<string, string>>): string {
    if (body.trim() === "") {
        return "";
    }

    return convertSlackMrkdwnToPreviewHtml(body, emojiMap, { replaceAppDividers: true });
}

function convertSlackMrkdwnToPreviewHtml(
    text: string,
    emojiMap?: Readonly<Record<string, string>>,
    options?: { replaceAppDividers?: boolean }
): string {
    const lf = normalizeSlackPreviewNewlines(text);
    const blockNorm = collapseBlankLinesBeforeBlockquoteLines(normalizeSlackMrkdwnBlockQuotes(lf));
    const { text: maskedLinks, links } = maskSlackMrkdwnLinks(blockNorm);
    const { text: maskedAtx, atxHeaders } = maskSlackMrkdwnAtxHeaders(maskedLinks);
    const { text: masked, listItems } = maskSlackMrkdwnBulletListItems(maskedAtx);
    const toOpts = { hrefTarget: "_blank" as const };
    let html = toHTML(masked, toOpts);
    html = unmaskSlackMrkdwnAtxHeadersInHtml(html, atxHeaders, toOpts);
    html = unmaskSlackMrkdwnLinksInHtml(html, links);
    html = repairEscapedMrkdwnBlockquotesInHtml(html);
    html = compactSlackDeliveryPreviewHtmlBreaks(html);
    if (options?.replaceAppDividers) {
        html = replaceActivityLogAppDividerLineWithHr(html);
    }
    html = unmaskSlackMrkdwnBulletListItemsInHtml(html, listItems, links, atxHeaders, toOpts);
    html = applySlackWorkspaceEmojisToHtml(html, emojiMap);
    return html;
}

function renderImmediateContainerPreview(
    subject: string,
    body: string,
    emojiMap?: Readonly<Record<string, string>>
): string {
    const titleHtml = slackMrkdwnFragmentToPreviewHtml(`*${subject}*`, emojiMap);
    const bodyHtml = renderSlackBodyHtml(body, emojiMap);
    return `<div class="ma-ae-slack-preview-container" role="group">
<div class="ma-ae-slack-preview-container__title">${titleHtml}</div>
${bodyHtml ? `<div class="ma-ae-slack-preview-container__body">${bodyHtml}</div>` : ""}
</div>`;
}

/** Slack mrkdwn → HTML for the delivery record Message tab (used by tests). */
export function slackMrkdwnToDeliveryRecordPreviewHtml(
    raw: string,
    emojiMap?: Readonly<Record<string, string>>,
    explicitSubject?: string
): string {
    if (isContainerStyleSummaryPreview(raw)) {
        return renderContainerStyleSummaryPreview(raw, emojiMap);
    }

    const { subject, body } = resolveImmediatePreviewParts(raw, explicitSubject);
    if (subject) {
        return renderImmediateContainerPreview(subject, body, emojiMap);
    }

    const html = renderSlackBodyHtml(body, emojiMap);
    return html ? `<div class="ma-ae-slack-preview-body">${html}</div>` : "";
}

/** Collapsible-container batch summaries (emoji + *App - N Notifications* + :bell_ringer:). */
export function isContainerStyleSummaryPreview(raw: string): boolean {
    return (
        /:bell_ringer:/.test(raw) &&
        /\*[^*\n]*-\s*\d+\s+Notifications?\s*\*/i.test(raw)
    );
}

function renderContainerStyleSummaryPreview(
    raw: string,
    emojiMap?: Readonly<Record<string, string>>
): string {
    const normalized = normalizeSlackPreviewNewlines(raw).trim();
    const dividerPattern = new RegExp(`\\n*${BoxDrawingLightHorizontal}{8,}\\n*`, "g");
    const chunks = normalized
        .split(dividerPattern)
        .map((chunk) => chunk.trim())
        .filter((chunk) => chunk.length > 0);

    const parts: string[] = [];
    for (const chunk of chunks) {
        if (isContainerStyleSummaryPreview(chunk)) {
            parts.push(renderOneContainerPreview(chunk, emojiMap));
            continue;
        }

        const { body, buttonLabel } = splitTrailingDetailsButton(chunk);
        if (body) {
            parts.push(
                `<div class="ma-ae-slack-preview-summary-footer">${slackMrkdwnFragmentToPreviewHtml(body, emojiMap)}</div>`
            );
        }
        if (buttonLabel) {
            parts.push(
                `<div class="ma-ae-slack-preview-summary-actions"><span class="ma-ae-slack-preview-summary-button" role="presentation">${escapeHtmlAttribute(buttonLabel)}</span></div>`
            );
        }
    }

    return parts.join("");
}

function splitTrailingDetailsButton(chunk: string): { body: string; buttonLabel?: string } {
    const lines = chunk.split("\n");
    const last = (lines[lines.length - 1] ?? "").trim();
    const unwrapped = last.replace(/^\*+/, "").replace(/\*+$/, "").trim();
    if (/^View All Message Details$/i.test(unwrapped)) {
        const body = lines.slice(0, -1).join("\n").trim();
        return { body, buttonLabel: unwrapped };
    }
    return { body: chunk.trim() };
}

function findContainerBodyStart(lines: string[]): number {
    for (let i = 0; i < lines.length; i++) {
        const trimmed = lines[i].trim();
        if (/^•/.test(trimmed) || /^_[^_\n].*_$/.test(trimmed) || /^>/.test(trimmed)) {
            return i;
        }
    }
    return lines.length;
}

function renderOneContainerPreview(
    chunk: string,
    emojiMap?: Readonly<Record<string, string>>
): string {
    const lines = chunk.split("\n");
    const bodyStart = findContainerBodyStart(lines);
    const titleText = lines.slice(0, bodyStart).join("\n").trim();
    const bodyText = lines.slice(bodyStart).join("\n").trim();
    const titleHtml = titleText ? slackMrkdwnFragmentToPreviewHtml(titleText, emojiMap) : "";
    const bodyHtml = bodyText ? slackMrkdwnFragmentToPreviewHtml(bodyText, emojiMap) : "";

    return `<div class="ma-ae-slack-preview-container" role="group">
<div class="ma-ae-slack-preview-container__title">${titleHtml}</div>
${bodyHtml ? `<div class="ma-ae-slack-preview-container__body">${bodyHtml}</div>` : ""}
</div>`;
}

